if (!customElements.get('product-media')) {
  class ProductMedia extends HTMLElement {
    constructor() {
      super();
      this.isDesktopSlider = this.classList.contains('product__slider-slider-grid');
      this.slides = this.querySelectorAll('.product__slide');
      this.isNav = this.dataset.type == 'nav';
      this.cellSelector = this.isNav ? 'js-thumb-slide' : 'js-main-slide';
      this.style.display = 'flex';

      // Track current media URL to prevent unnecessary updates
      this.currentMediaUrl = null;
      this.isUpdating = false;

      // Immediately add js-main-slide class to prevent layout shift
      this.initializeSlides();
    }

    static get observedAttributes() {
      return ['data-media-url'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === 'data-media-url' && oldValue !== newValue && newValue) {
        this.updateMedia(newValue);
      }
    }

    initializeSlides() {
      // Add the js-main-slide class immediately to prevent slides from being hidden initially
      this.slides.forEach((slide, index) => {
        if (!this.isNav) {
          slide.classList.add('js-main-slide');
          if (index === 0) {
            slide.classList.add('first-main-slide');
          }
        } else {
          slide.classList.add('js-thumb-slide');
        }
      });
    }

    connectedCallback() {
      this.initScrollEffect();

      // Check for initial media URL
      const initialMediaUrl = this.dataset.mediaUrl;
      if (initialMediaUrl) {
        this.currentMediaUrl = initialMediaUrl;
      }
    }

    /**
     * Public method for parent components to update media
     * @param {string} mediaUrl - The URL to fetch new media content
     * @param {Object} options - Optional configuration
     */
    async updateMedia(mediaUrl, options = {}) {
      // Prevent unnecessary updates or concurrent updates
      if (!mediaUrl || mediaUrl === this.currentMediaUrl || this.isUpdating) {
        return;
      }

      this.isUpdating = true;
      this.currentMediaUrl = mediaUrl;

      try {
        // Dispatch update start event
        this.dispatchEvent(new CustomEvent('product-media:update-start', {
          bubbles: true,
          detail: { mediaUrl, options }
        }));

        if (this.allowDebug()) {
          console.log(`🖼️ Product Media: Updating media`, mediaUrl);
        }

        // Apply loading state
        this.setLoadingState(true);

        // Fetch new media content
        const response = await fetch(mediaUrl);
        if (!response.ok) throw new Error(`Failed to fetch media: ${response.status}`);

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newMediaComponent = doc.querySelector('product-media');

        if (!newMediaComponent) {
          throw new Error('No product-media component found in response');
        }

        // Count images in the new gallery
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = newMediaComponent.innerHTML;
        const newImages = tempContainer.querySelectorAll('img');
        const isLargeGallery = newImages.length > 3;

        // Use enhanced transitions for large galleries, simple for small
        if (isLargeGallery) {
          await this.applyEnhancedTransition(newMediaComponent.innerHTML, newImages);
        } else {
          await this.applySimpleTransition(newMediaComponent.innerHTML);
        }

        // Dispatch success event
        this.dispatchEvent(new CustomEvent('product-media:update-success', {
          bubbles: true,
          detail: { mediaUrl, isLargeGallery, imageCount: newImages.length }
        }));

        if (this.allowDebug()) {
          console.log('✅ Product Media: Successfully updated media');
        }

      } catch (error) {
        console.error('❌ Product Media: Update failed:', error);

        // Dispatch error event
        this.dispatchEvent(new CustomEvent('product-media:update-error', {
          bubbles: true,
          detail: { mediaUrl, error: error.message }
        }));

      } finally {
        this.setLoadingState(false);
        this.isUpdating = false;
      }
    }

    /**
     * Simple, fast transition for small galleries
     */
    async applySimpleTransition(newContent) {
      return new Promise((resolve) => {
        this.style.transition = 'opacity 0.15s ease-out';
        this.style.opacity = '0.7';

        setTimeout(() => {
          this.innerHTML = newContent;
          this.reinitialize();

          setTimeout(() => {
            this.style.opacity = '1';

            setTimeout(() => {
              this.style.transition = '';
              this.style.opacity = '';
              resolve();
            }, 150);
          }, 50);
        }, 75);
      });
    }

    /**
     * Enhanced transition with preloading for large galleries
     */
    async applyEnhancedTransition(newContent, images) {
      return new Promise((resolve) => {
        const executeTransition = async () => {
          try {
            // Preload images for smoother transition
            if (images && images.length > 1) {
              await this.preloadImages(images);
            }

            // Apply enhanced transition
            this.style.transition = 'opacity 0.3s ease-out';
            this.style.opacity = '0';

            setTimeout(() => {
              this.innerHTML = newContent;
              this.reinitialize();

              setTimeout(() => {
                this.style.opacity = '1';

                setTimeout(() => {
                  this.style.transition = '';
                  this.style.opacity = '';
                  resolve();
                }, 300);
              }, 100);
            }, 150);

          } catch (error) {
            console.error('❌ Product Media: Enhanced transition error:', error);
            // Fallback to simple transition
            await this.applySimpleTransition(newContent);
            resolve();
          }
        };

        executeTransition();
      });
    }

    /**
     * Preload images for smoother transitions
     */
    async preloadImages(images) {
      const imagePromises = Array.from(images).map((img) => {
        return new Promise((resolve) => {
          if (!img.src && !img.dataset.src) {
            resolve();
            return;
          }

          const preloadImg = new Image();
          preloadImg.onload = () => resolve();
          preloadImg.onerror = () => resolve(); // Resolve anyway
          preloadImg.src = img.src || img.dataset.src;

          // Timeout to prevent hanging
          setTimeout(() => resolve(), 500);
        });
      });

      try {
        const preloadTimeout = new Promise(resolve => setTimeout(resolve, 400));
        await Promise.race([Promise.all(imagePromises), preloadTimeout]);
      } catch (error) {
        // Continue anyway
      }
    }

    /**
     * Set loading state visual feedback
     */
    setLoadingState(isLoading) {
      this.classList.toggle('product-media--loading', isLoading);
      this.style.opacity = isLoading ? '0.8' : '';
    }

    /**
     * Reinitialize component after content change
     */
    reinitialize() {
      // Update slides reference
      this.slides = this.querySelectorAll('.product__slide');

      // Re-add slide classes
      this.initializeSlides();

      // Reinitialize scroll effects
      this.initScrollEffect();
    }

    initScrollEffect() {
      // Check if viewport width is more than 767px and if isDesktopSlider is false
      if (window.innerWidth > 767 && !this.isDesktopSlider) {
        return; // exit the function. This means we are on a large screen and we do not want to render slider mode.
      }

      this.activeSlides = this.querySelectorAll(`.${this.cellSelector}`);
      let columnDivider = 2;
      let scrollContainer = window;

      if(this.activeSlides.length <= 0) {
        this.activeSlides = this.querySelectorAll(`.product__slide`);
        this.activeSlides.forEach((image) => image.classList.add('js-main-slide'));
      }

      if(this.closest('quick-add-modal')){
        columnDivider = 1;
        scrollContainer = this.closest('.quick-add-modal__content')
      }

      // add indicators
      const totalIndicators = Math.round(this.activeSlides.length / columnDivider);

      this.querySelector('.indicators')?.remove();
      if(totalIndicators > 1) {
        let indicatorsDiv = document.createElement('ul');
        indicatorsDiv.classList.add('indicators');

        for(var i= 1; i <= totalIndicators; i++) {
          indicatorsDiv.innerHTML += `<li class="${i === 1 ? 'current' : ''}"></li>`;
        }

        this.appendChild(indicatorsDiv);
      }

      // Clean up old scroll listeners
      if (this.scrollHandler) {
        const oldScrollContainer = this.lastScrollContainer || window;
        oldScrollContainer.removeEventListener('scroll', this.scrollHandler);
      }

      // Set up new scroll listener
      this.currentPage = 1;
      this.lastScrollContainer = scrollContainer;

      // Simple throttled scroll handler for better performance
      let scrollTimeout;
      this.scrollHandler = () => {
        if (scrollTimeout) return;

        scrollTimeout = setTimeout(() => {
          this.handleScrollUpdate(scrollContainer);
          scrollTimeout = null;
        }, 16); // ~60fps for smooth updates
      };

      scrollContainer.addEventListener('scroll', this.scrollHandler);
    }

    handleScrollUpdate(scrollContainer) {
      let scrollPos = scrollContainer.pageYOffset;
      let found = false;
      this.activeSlides.forEach((slide) => {
        if(!found && slide.offsetTop >= scrollPos) {
          found = true;
          this.activeSlides.forEach((__slide, index) => {
            __slide.classList.remove('currentpage');
            if(__slide === slide) {
              this.currentPage = Math.round((index + 1) / 2); // Assuming columnDivider = 2
              slide.classList.add('currentpage');
            }
          });

          this.updateIndicators();
        }
      });
    }

    updateIndicators() {
      let indicatorsLi = this.querySelectorAll('.indicators li');

      // remove current class from previous page
      indicatorsLi?.forEach((li) => li.classList.remove('current'));

      // add current class to current page
      indicatorsLi[this.currentPage - 1]?.classList.add('current');
    }

    /**
     * Cleanup when component is removed
     */
    disconnectedCallback() {
      if (this.scrollHandler && this.lastScrollContainer) {
        this.lastScrollContainer.removeEventListener('scroll', this.scrollHandler);
      }
    }

    /**
     * Debug helper
     */
    allowDebug() {
      return this.closest('[data-debug]') !== null || window.location.search.includes('debug=1');
    }
  }

  customElements.define('product-media', ProductMedia);
}
